/**
 * Life4Earth V2 — Admin Role Catalogue · Add Role to Bundle tray.
 *
 * DEV-1214 iter 3. Slide-in picker that lets the sysadmin attach an
 * existing role_definition to the currently-viewed Role Bundle.
 *
 * Locked design micro-decision A (Skip 2026-06-04):
 *   * Default-filtered to roles whose pillar matches the bundle's pillar(s),
 *     derived from applicable_entity_types via ENTITY_TYPE_CATALOG.
 *   * "Show all pillars" override surfaces roles from sibling pillars
 *     (useful when a bundle spans multiple entity_types and the admin
 *     wants to pull in a role from a sibling pillar).
 *
 * Excludes roles already attached to ANY bundle (a role can only belong
 * to one bundle at a time per role_definitions.role_bundle_guid being
 * a single nullable column).
 *
 * Calls POST /api/admin/role-definitions/{guid}/attach-bundle for each
 * pick — multi-select is supported (admin picks several, hits "Attach N
 * roles", server-side calls fire sequentially, parent re-fetches).
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  attachRoleBundleToDefinition,
  type RoleBundleResponse,
} from "../../api/roleBundles";
import {
  fetchAdminCatalog,
  type AdminCatalogRole,
} from "../../api/roleRhythms";
import {
  ENTITY_TYPE_CATALOG,
  getEntityTypeMeta,
} from "../../types/entityTypes";
import styles from "./AddRoleTray.module.css";

/**
 * DEV-1214 iter 3. Map an EntityTypeMeta.pillar value
 * ('org' | 'geo' | 'project' | 'workspace') to the corresponding
 * role_definition pillar_type
 * ('org_community' | 'geo_community' | 'full_project' | 'workspace' | 'solution').
 *
 * Solution pillar has no entity_type mapping today — it stays empty until
 * solution-pillar entities exist.
 */
const ENTITY_PILLAR_TO_ROLE_PILLAR: Record<string, string> = {
  org:       "org_community",
  geo:       "geo_community",
  project:   "full_project",
  workspace: "workspace",
};

interface Props {
  bundle: RoleBundleResponse;
  open: boolean;
  onClose: () => void;
  /** Fires after at least one role was attached so the parent can refresh. */
  onAttached: () => void;
}

export default function AddRoleToBundleTray({
  bundle,
  open,
  onClose,
  onAttached,
}: Props) {
  const [allRoles, setAllRoles] = useState<AdminCatalogRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showAllPillars, setShowAllPillars] = useState(false);
  const [selectedGuids, setSelectedGuids] = useState<Set<string>>(new Set());
  const [attaching, setAttaching] = useState(false);

  // Fetch the full role catalog on open. Filtering happens in-memory.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAdminCatalog()
      .then((res) => {
        if (cancelled) return;
        // Flatten pillars → roles for a single search/filter list.
        const flat: AdminCatalogRole[] = [];
        for (const p of res.pillars) flat.push(...p.roles);
        setAllRoles(flat);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load role catalog.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Reset selection + draft state on open/close to avoid stale selections
  // bleeding across opens.
  useEffect(() => {
    if (open) {
      setSelectedGuids(new Set());
      setSearch("");
      setShowAllPillars(false);
      setError(null);
    }
  }, [open]);

  // ── Compute the bundle's pillar set from applicable_entity_types ────

  const bundlePillars = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const et of bundle.applicableEntityTypes) {
      const meta = getEntityTypeMeta(et);
      if (!meta) continue;
      const rolePillar = ENTITY_PILLAR_TO_ROLE_PILLAR[meta.pillar];
      if (rolePillar) set.add(rolePillar);
    }
    return set;
  }, [bundle.applicableEntityTypes]);

  /** True when the bundle has no entity_type restriction (so we can't
   *  derive pillars). In that case, default to "show all pillars" since
   *  any role could be a candidate. */
  const bundleHasNoPillarHint = bundlePillars.size === 0;

  // ── Filter visible roles ────────────────────────────────────────────

  const visibleRoles = useMemo<AdminCatalogRole[]>(() => {
    const q = search.trim().toLowerCase();
    const effectiveShowAll = showAllPillars || bundleHasNoPillarHint;

    return allRoles
      .filter((r) => {
        // Exclude roles already attached to any bundle (this one or another).
        if (r.roleBundleGuid) return false;
        // Pillar filter unless overridden.
        if (!effectiveShowAll && !bundlePillars.has(r.pillarType)) return false;
        // Text search.
        if (q && !(
          r.label.toLowerCase().includes(q)
          || (r.description ?? "").toLowerCase().includes(q)
          || r.roleCategory.toLowerCase().includes(q)
          || r.pillarType.toLowerCase().includes(q)
        )) return false;
        return true;
      })
      .sort((a, b) => {
        // Pillar order first (matches AdminRoleRhythms), then label.
        if (a.pillarType !== b.pillarType) {
          return a.pillarType.localeCompare(b.pillarType);
        }
        return a.label.localeCompare(b.label);
      });
  }, [allRoles, search, showAllPillars, bundlePillars, bundleHasNoPillarHint]);

  // ── Selection helpers ───────────────────────────────────────────────

  const toggleRole = (guid: string) => {
    setSelectedGuids((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  };

  const handleAttach = async () => {
    if (selectedGuids.size === 0 || attaching) return;
    setAttaching(true);
    setError(null);
    try {
      // Sequential attaches — server enforces sysadmin auth per call;
      // doing them in order makes error attribution clean if one fails.
      for (const guid of selectedGuids) {
        await attachRoleBundleToDefinition(guid, bundle.roleBundleGuid);
      }
      onAttached();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to attach one or more roles.");
    } finally {
      setAttaching(false);
    }
  };

  if (!open) return null;

  // ── Render ──────────────────────────────────────────────────────────

  // Group visible roles by pillar for clean section rendering.
  const byPillar = new Map<string, AdminCatalogRole[]>();
  for (const role of visibleRoles) {
    const list = byPillar.get(role.pillarType) ?? [];
    list.push(role);
    byPillar.set(role.pillarType, list);
  }

  const bundlePillarLabels = Array.from(bundlePillars)
    .map(pillarFriendlyLabel)
    .join(", ");

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={() => { if (!attaching) onClose(); }}
        aria-hidden="true"
      />
      <aside
        className={styles.tray}
        role="dialog"
        aria-labelledby="add-role-to-bundle-title"
        style={{ maxWidth: 560 }}
      >
        <header className={styles.header}>
          <div>
            <div className={styles.eyebrow}>{bundle.label}</div>
            <h2 id="add-role-to-bundle-title" className={styles.title}>
              Add roles to bundle
            </h2>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            disabled={attaching}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.body}>
          <input
            type="search"
            className={styles.textInput}
            placeholder="Search roles by name, category, or pillar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {!bundleHasNoPillarHint && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: "var(--text-secondary)",
                marginBlockStart: 8,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={showAllPillars}
                onChange={(e) => setShowAllPillars(e.target.checked)}
              />
              Show all pillars
              <span style={{ color: "var(--text-muted)", marginInlineStart: 4 }}>
                (default: pillar matches bundle —{" "}
                <code>{bundlePillarLabels || "none derived"}</code>)
              </span>
            </label>
          )}

          {bundleHasNoPillarHint && (
            <div style={{ marginBlockStart: 8, fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
              This bundle has no applicable entity types set, so all pillars are visible.
            </div>
          )}

          <div style={{ marginBlockStart: 12 }}>
            {loading && allRoles.length === 0 && (
              <div className={styles.fieldHint}>Loading role catalog…</div>
            )}
            {!loading && visibleRoles.length === 0 && (
              <div className={styles.fieldHint} style={{ fontStyle: "italic" }}>
                No unattached roles match the current filter.
                {!showAllPillars && !bundleHasNoPillarHint && " Try toggling Show all pillars."}
              </div>
            )}

            {Array.from(byPillar.entries()).map(([pillar, roles]) => (
              <section key={pillar} style={{ marginBlockEnd: 12 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    marginBlockEnd: 4,
                  }}
                >
                  {pillarFriendlyLabel(pillar)}
                  <span style={{ marginInlineStart: 6, color: "var(--text-muted)" }}>
                    ({roles.length})
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {roles.map((role) => {
                    const checked = selectedGuids.has(role.definitionGuid);
                    return (
                      <label
                        key={role.definitionGuid}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8,
                          padding: "6px 10px",
                          border: "1px solid var(--border-default)",
                          borderRadius: 4,
                          background: checked ? "var(--bg-elevated)" : "var(--bg-surface)",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRole(role.definitionGuid)}
                          style={{ marginBlockStart: 2 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: "var(--text-primary)" }}>
                            {role.label}
                            <span style={{ marginInlineStart: 8, color: "var(--text-muted)", fontSize: 11 }}>
                              {role.roleCategory}
                            </span>
                          </div>
                          {role.description && (
                            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBlockStart: 2 }}>
                              {role.description}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onClose}
            disabled={attaching}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleAttach}
            disabled={attaching || selectedGuids.size === 0}
          >
            {attaching
              ? "Attaching…"
              : selectedGuids.size === 0
                ? "Attach roles"
                : `Attach ${selectedGuids.size} role${selectedGuids.size === 1 ? "" : "s"}`}
          </button>
        </footer>
      </aside>
    </>
  );
}

function pillarFriendlyLabel(pillarType: string): string {
  switch (pillarType) {
    case "org_community": return "Org / Enabler";
    case "geo_community": return "Geographic";
    case "full_project":  return "Full Project";
    case "workspace":     return "Workspace";
    case "solution":      return "Solution";
    default:              return pillarType;
  }
}

// Suppresses "imported but unused" lint warning for the catalog import
// which is only referenced inside the bundle-pillar derivation above.
// (Kept as an explicit import so future maintenance has the symbol in
// scope when adding more catalog-derived behavior.)
void ENTITY_TYPE_CATALOG;
